/* DeviceInstanceEditor.js */

const _ = require("lodash");
const GameEditor = require("@gluenetworks/game_editor");
const Registrations = require("../../../RegistrationNamespaces");
const PagingFeed = require("./views/snapshot/PagingDeviceConfigurationSnapshotFeed");
const {applyExposureLicenseGate} = require("../../lib/exposureLicenseGate");
const CsvSerializer = require("../../lib/CsvSerializer");

class DeviceInstanceEditor extends GameEditor {
    constructor(
        logger,
        di,
        registeredDetails,
        editorState,
        editorLayoutMgr,
        editorLayout,
        VendorApiConfigEntity,
        DeviceEntity,
        userSession,
        DeviceConfigEntity,
        editorTabCmpnt,
        solutionsManager,
        OsVersionEntity,
        steveIo,
        ConfigAuditPolicy,
        storageFactory,
        CommandSetEntity,
        ValueMap,
        ConstructTypeEntity,
        licenseRestriction
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._licenseRestriction = licenseRestriction;
        this._registeredDetails = registeredDetails;
        this._editorLayout = editorLayout;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._isMuted = false;
        this._isActiveTab = true;
        this._valueMap = ValueMap;
        this._steveIo = steveIo;
        this._userSession = userSession;
        this._activeOrgId = userSession.activeOrganization.id;
        this._ConfigAuditPolicyEntity = ConfigAuditPolicy;
        const solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = solutionDefinition.name || "";
        const deviceEditorViews = _.get(solutionDefinition, "deviceEditor.viewNamespaces", []);
        const deviceEditorActions = _.get(solutionDefinition, "deviceEditor.actionNamespaces", []);
        const launchOptions = editorState.launchOptions;
        this._auditPolicyId = undefined;

        this._osIsSupported = false;
        this._supportedOsList = [];

        this.currentEntityRemoved = false;
        this.currentEntityDeleteInProgress = false;
        this._commandSetEntityDef = CommandSetEntity;
        this._commandSetMap = {};
        this._constructTypeEntityDef = ConstructTypeEntity;

        this.sendToDeviceIfEditorNotAvailable = _.debounce(
            this.sendToDeviceIfEditorNotAvailable.bind(this),
            500
        );

        this._storageFactory = storageFactory;
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "view",
            "device_details"
        );

        this._networkDiscovery = false;
        if (_.get(launchOptions, "type", false) === "NetworkDiscovery") {
            this._networkDiscovery = true;
            this._activeOrgId = _.get(launchOptions, "deviceIds.0", this._activeOrgId);
        }

        this.setNamespaceGlobs(deviceEditorViews.concat(deviceEditorActions), true);

        this._VendorApiConfigEntityDef = VendorApiConfigEntity;
        this._isCiscoSupportEnabled = undefined;
        this._instanceEditorState = {};
        this._DeviceEntity = DeviceEntity;
        this._OsVersionEntity = OsVersionEntity;
        this._osVersionFeed = undefined;
        this._deviceProm = undefined;
        this._deviceFeed = undefined;
        this._device = undefined;
        this._osVersion = undefined;
        this._tabId = launchOptions.id || "Unknown";
        this._deviceName = launchOptions.title || "Unknown";
        this._deviceType = "unknown";
        this._deviceTypeIcon = "common.default_device";

        this._typeBaseGroupMap = _.get(this._DeviceEntity, "typeBaseGroupMap");

        this._DeviceConfigEntity = DeviceConfigEntity;
        this._deviceConfigFeed = undefined;
        this._deviceConfigFeedProm = undefined;

        this.retardActions(true);

        this._muteTimeoutId = null;
        this._boundOnDocumentStateChange = this._onDocumentStateChange.bind(this);
        this._debouncedOnActiveTabChange = this._debounceWithArgs(
            this._onActiveTabChange.bind(this),
            500
        );
        this._debouncedOnActiveTabOpened = this._debounceWithArgs(
            this._onActiveTabOpened.bind(this),
            500
        );

        this._onBackupOnChanged = this._onBackupOnChanged.bind(this);

        /*
        const fields = [
            "name",
            "description",
            "managementState",
            "environment",
            "siteId",
            "allowSiteAutoAssignment",
            "connectionObject",
            "latestHaveIChangedLastChecked",
            "capturedBy",
            "discoveredOn",
            "discoveryStatus",
            "discoveredBy",
            "engineZoneId",
            "detectedVendor",
            "detectedTypeBase",
            "detectedHostName",
            "detectedOs",
            "detectedSoftwareVersion",
            "detectedSerialNumber",
            "detectedSku",
            "detectedUptime",
            "fileServerId",
            "vrf",
            "useIssu",
            "lockEngine",
            "updatedOn",
            "updatedBy",
            "lastActivityOn",
            "lastActivityBy",
            "lastActivityType",
            "lastActivityError",
            "discoveryLevel",
            "customFieldValues",
            "ip",
            "latestHaveIChanged",
            "elementCount",
            "configuredBy",
            "configuredInfo",
            "configuredOn",
            "configuredType",
            "fileServerIdentifier",
            "fileServerName",
            "haPairName",
            ].reduce((fields, field) => Object.assign(fields, { [field]: true }), {});
         */

        // If we define the launching entity id
        const deviceIds = [].concat(launchOptions.deviceIds || []);
        if (!this._networkDiscovery) {
            // Document listener to mute/unmute device feeds not required with multi-edit or network discovery
            if (deviceIds.length === 1) {
                this._registerDocumentStateListener();
            }
            this._initDeviceProm(deviceIds);
        } else {
            // this._hideBarViews.bind(this);
            const hideBarDebounced = _.debounce(this._hideBarViews.bind(this), 500);
            hideBarDebounced();
        }

        this._registerViewStateListener();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerDocumentStateListener() {
        document.addEventListener("visibilitychange", this._boundOnDocumentStateChange);
        this._editorLayout.on("ActiveTabChanged", this._debouncedOnActiveTabChange);
        this._editorLayout.on("ActiveTabOpened", this._debouncedOnActiveTabOpened);
    }

    _unregisterDocumentStateListener() {
        document.removeEventListener("visibilitychange", this._boundOnDocumentStateChange);
        this._editorLayout.removeListener("ActiveTabChanged", this._debouncedOnActiveTabChange);
        this._editorLayout.removeListener("ActiveTabOpened", this._debouncedOnActiveTabOpened);
    }

    _debounceWithArgs(func, wait) {
        let lastArgs = null;
        let lastCallTime = null;

        return function (...args) {
            const now = new Date().getTime();
            const isSameArgs =
                lastArgs &&
                args.length === lastArgs.length &&
                args.every((arg, index) => arg === lastArgs[index]);

            if (isSameArgs && now - lastCallTime < wait) {
                // If the function is called with the same arguments within the wait period, ignore this call
                return;
            }

            // Otherwise, execute the function and update lastArgs and lastCallTime
            lastCallTime = now;
            lastArgs = args;
            func.apply(this, args);
        };
    }

    _onDocumentStateChange() {
        if (document.visibilityState === "visible") {
            if (this._isActiveTab) {
                this._unmuteDeviceFeed();
            }
        } else {
            this._muteDeviceFeed();
        }
    }

    _onActiveTabChange(activeTabId) {
        this._changeDeviceFeedState(activeTabId);
    }

    _onActiveTabOpened(activeTabId) {
        this._changeDeviceFeedState(activeTabId);
    }

    _changeDeviceFeedState(activeTabId) {
        if (this._tabId === activeTabId) {
            this._isActiveTab = true;
            this._unmuteDeviceFeed();
        } else {
            this._isActiveTab = false;
            this._muteDeviceFeed();
        }
    }

    _muteDeviceFeed() {
        if (!this.muteTimeoutId) {
            this.muteTimeoutId = setTimeout(() => {
                if (!this._isMuted) {
                    if (this._device) {
                        if (!this._device.isDirty()) {
                            this._deviceFeed.deactivate();
                            this._isMuted = true;
                        }
                    } else {
                        // Delaying until device feed is ready
                        this.whenDeviceIsReady().then(() => {
                            this._deviceFeed.deactivate();
                            this._isMuted = true;
                        });
                    }
                }

                this.muteTimeoutId = null;
            }, 60000);
        }
    }

    _unmuteDeviceFeed() {
        if (this.muteTimeoutId) {
            clearTimeout(this.muteTimeoutId);
            this.muteTimeoutId = null;
        }
        if (this._isMuted) {
            this._deviceFeed.activate();
            this._reestablishDeviceFeed();
            this._isMuted = false;
        }
    }

    _reestablishDeviceFeed() {
        this._deviceFeed.reestablishFeed(this._activeOrgId, {id: this._device._id});
        this._deviceFeed.whenInitialized().then(() => {
            if (this._deviceFeed) {
                if (this._deviceFeed.getNumOfCachedInstances() === 1) {
                    this._device = this._deviceFeed.getInstanceByIndex(0);

                    this._initDeviceProm([this._device.getId()]);

                    const promises = [
                        // _.invoke(this._pagingFeed, "shutdown") || Promise.resolve(), // Same as ._deviceConfigFeed
                        _.invoke(this._deviceConfigFeed, "shutdown") || Promise.resolve(),
                        _.invoke(this._osVersionFeed, "shutdown") || Promise.resolve(),
                    ];

                    return Promise.all(promises)
                        .then(() => {
                            return this._deviceProm;
                        })
                        .then(() => {
                            this.setEditorState({fqn: this._editorState.fqn}, true);
                        });
                } else if (this._deviceFeed.getNumOfCachedInstances() === 0) {
                    this._onDeviceRemoved();
                } else {
                    this._logger.warn({}, "DeviceInstanceEditor._reestablishDeviceFeed error.");
                }
            } else {
                this._logger.warn({}, "DeviceInstanceEditor._reestablishDeviceFeed error.");
            }
        });
    }

    _registerViewStateListener() {
        this.on("ClearViewState", (this._onClearViewState = this._onClearViewState.bind(this)));
        this._cabinetCmpnt.on(
            "SelectView",
            (this._onSelectViewEditor = this._onSelectViewEditor.bind(this))
        );
    }

    _unregisterCabinetListener() {
        this._cabinetCmpnt.removeListener("SelectView", this._onSelectViewEditor);
    }

    _unregisterViewStateListener() {
        // this.removeAllListeners("DeviceConfigShutdown");
        this.removeAllListeners("ClearViewState");
    }

    _onSelectViewEditor(e) {
        if (this.currentSolutionName === "config_drift") {
            this._userDataStorage.setData(e, "CD_active_view");
        } else if (this.currentSolutionName === "os_manager") {
            this._userDataStorage.setData(e, "OSM_active_view");
        } else if (this.currentSolutionName === "data_explorer") {
            this._userDataStorage.setData(e, "DE_active_view");
        } else {
            this._userDataStorage.setData(e, "DM_active_view");
        }
    }

    _registerListenersOnDevice() {
        this._device.on(
            "NamePropertyUpdated",
            (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
        );
        this._device.on(
            "LastRestoredProgressPropertyUpdated",
            (this._onLastRestoredProgressUpdated = this._onLastRestoredProgressUpdated.bind(this))
        );
        this._device.on(
            "DetectedTypeBasePropertyUpdated",
            (this._onDetectedTypeBasePropertyUpdated =
                this._onDetectedTypeBasePropertyUpdated.bind(this))
        );
        this._device.on(
            "EntityDeletedFromSystem",
            (this._onDeviceRemoved = this._onDeviceRemoved.bind(this))
        );
        this._device.on("LastBackupOnPropertyUpdated", this._onBackupOnChanged);
        this._device.on("LastRestoredOnPropertyUpdated", this._onBackupOnChanged);
    }

    _unregisterListenersOnDevice() {
        this._device.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
        this._device.removeListener(
            "LastRestoredProgressPropertyUpdated",
            this._onLastRestoredProgressUpdated
        );
        this._device.removeListener(
            "DetectedTypeBasePropertyUpdated",
            this._onDetectedTypeBasePropertyUpdated
        );
        this._device.removeListener("EntityDeletedFromSystem", this._onDeviceRemoved);
        this._device.removeListener("LastBackupOnPropertyUpdated", this._onBackupOnChanged);
        this._device.removeListener("LastRestoredOnPropertyUpdated", this._onBackupOnChanged);
    }

    _registerVersionFeedListeners() {
        this._osVersionFeed.on(
            "EntityInitialized",
            (this._updateVersionEntity = this._updateVersionEntity.bind(this))
        );
        this._osVersionFeed.on(
            "EntityAdded",
            (this._updateVersionEntity = this._updateVersionEntity.bind(this))
        );
        this._osVersionFeed.on(
            "EntityPropertiesUpdated",
            (this._updateVersionEntity = this._updateVersionEntity.bind(this))
        );
    }

    _unregisterVersionFeedListeners() {
        this._osVersionFeed.removeListener("EntityInitialized", this._updateVersionEntity);
        this._osVersionFeed.removeListener("EntityAdded", this._updateVersionEntity);
        this._osVersionFeed.removeListener("EntityPropertiesUpdated", this._updateVersionEntity);
    }

    _updateVersionEntity(entity) {
        this._osVersion = entity;
    }

    _onClearViewState() {
        _.delay(() => (this.editorState.viewState = undefined), 50);
    }

    _onNamePropertyUpdated() {
        this.updateTab(this._device.pristineProperties.name);
    }

    _onLastRestoredProgressUpdated(val) {
        this.emit("RestoreProgressUpdate", val);
    }

    _onDetectedTypeBasePropertyUpdated() {
        const pristineDevice = this._device.pristineProperties.detectedTypeBase;
        this.updateTabIcon(
            pristineDevice === "unknown"
                ? this._deviceTypeIcon
                : "common." +
                      _.snakeCase(this._typeBaseGroupMap[pristineDevice] || "default_device")
        );
    }

    _onBackupOnChanged() {
        this.emit("BackupsUpdated");
    }

    _onDeviceRemoved() {
        this.currentEntityRemoved = true;
        if (!this.currentEntityDeleteInProgress) {
            this.currentEntityRemoved = false;
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    _initTheOsVersion(_device) {
        this._osVersionFeed = _device.startRelatedOsVersionFeed({});
        this._registerVersionFeedListeners();
        return this._osVersionFeed.whenInitialized();
    }

    _initDeviceProm(deviceIds) {
        if (deviceIds.length === 1) {
            const deviceId = deviceIds[0];
            if (!this._deviceFeed) {
                this._deviceFeed = this._DeviceEntity.startDevicesFeed(
                    this._activeOrgId,
                    {id: {$eq: deviceId}},
                    {},
                    {},
                    {skip: 0, limit: 1}
                );
            }
            const promises = [this._deviceFeed.whenInitialized()];
            const hasPermission = !!_.get(
                this._userSession,
                "_userEntity.authorizedActions"
            ).filter((action) => action === "deviceSupportDataRead").length;
            // promises = [this._DeviceEntity.retrieveDevice(this._activeOrgId, deviceId)];
            if (hasPermission) {
                promises.push(
                    this._VendorApiConfigEntityDef.loadVendorApiConfig(this._activeOrgId)
                );
            }
            this._deviceProm = Promise.allSettled(promises)
                .then(([, /*deviceFeedInitialized*/ vendorApiConfig]) => {
                    let device;
                    if (this._deviceFeed && this._deviceFeed.getNumOfCachedInstances() === 1) {
                        device = this._deviceFeed.getInstanceByIndex(0);
                    }
                    vendorApiConfig = _.get(vendorApiConfig, "value", undefined);
                    if (this._solutionName === "device_manager" && hasPermission) {
                        if (
                            _.get(vendorApiConfig, "properties.vendors.cisco.enabled", false) ===
                            true
                        ) {
                            this._isCiscoSupportEnabled = true;
                            /*this.addBarViews([
                                {fullyQualifiedName: Registrations.DIE_VIEW_EOX_BULLETIN, shown: true, icon: "devicemanager.cisco_eox", title: "EOX Bulletin", name: "EOX Bulletin", order: 2},
                                {fullyQualifiedName: Registrations.DIE_VIEW_ADVISORY_SUMMARY, shown: true, icon: "devicemanager.cisco_advisory_list", title: "Advisory Summary", order: 3},
                                {fullyQualifiedName: Registrations.DIE_VIEW_SMARTNET_DETAILS, shown: true, icon: "devicemanager.cisco_smartnet", title: "SmartNet Details", order: 4}
                            ]);*/
                        } else {
                            this._isCiscoSupportEnabled = false;
                            this._hideVendorSpecificBarViews("cisco");
                        }
                        if (
                            _.get(vendorApiConfig, "properties.vendors.nist.enabled", false) ===
                            true
                        ) {
                            this._isNistSupportEnabled = true;
                        } else {
                            this._isNistSupportEnabled = false;
                        }
                    }

                    if (device) {
                        this._device = device;

                        if (this._device.getDeviceRawCategory() === "iot") {
                            this.hideBarAction(Registrations.DIE_ACTION_DISCOVERY);
                        }

                        this._registerListenersOnDevice();
                        let filter = {};
                        if (
                            _.get(this._solutionManager, "_activeSolution.name") === "config_drift"
                        ) {
                            filter = !_.isEmpty(this._device)
                                ? {$and: [{deviceId: this._device.getId()}, {fromCapture: true}]}
                                : {};
                        } else {
                            filter = !_.isEmpty(this._device)
                                ? {deviceId: this._device.getId()}
                                : {};
                        }
                        this._pagingFeed = new PagingFeed({
                            feedKey: "deviceconf_feed",
                            userSession: this._userSession,
                            entityDef: this._device,
                            relatedEntityDef: this._DeviceConfigEntity,
                            sort: {_id: -1}, // default sort
                            filter,
                        });

                        if (this._device.getDeviceRawCategory() === "iot") {
                            this._hideViewsForIot();
                        } else {
                            if (!this._isNistSupportEnabled && !this._isCiscoSupportEnabled) {
                                this._hideVendorSpecificBarViews("advisory");
                            }
                        }

                        this._hideExposureViewWhenUnlicensed();

                        // We hide certain views based on solution:
                        if (
                            this._solutionName &&
                            (this._solutionName === "device_manager" ||
                                this._solutionName === "data_explorer")
                        ) {
                            if (
                                !this._isPropertyAllowed("detectedVendor") ||
                                this._device.getDetectedVendor() !== "Cisco"
                            ) {
                                this._hideVendorSpecificBarViews("cisco");
                            }

                            this._deviceConfigFeed =
                                this._device.startRelatedDiscoveredConfigFeed();
                            if (!this._deviceConfigFeed) {
                                this._deviceConfigFeed = this._pagingFeed.feed;
                            }
                        } else {
                            this._deviceConfigFeed = this._pagingFeed.feed;
                        }

                        this._deviceConfigFeedProm = this._deviceConfigFeed.whenInitialized();
                        this._deviceName = this._device.name;
                        this._deviceType = this._device.detectedTypeBase;
                        if (this._isPropertyAllowed("detectedTypeName")) {
                            this._deviceTypeName = this._device.detectedTypeName;
                        }

                        if (this._typeBaseGroupMap[this._deviceType] === undefined) {
                            this._deviceTypeIcon = "common.default_device";
                        } else {
                            this._deviceTypeIcon =
                                "common." + _.snakeCase(this._typeBaseGroupMap[this._deviceType]);
                        }

                        this._tabCmpnt.setEditorIconName(this._deviceTypeIcon);
                        this._tabCmpnt.setTitle(this._deviceName);
                        return this._pagingFeed.whenFeedReady();
                    } else {
                        this.closeEditor(this._tabId);
                        return Promise.reject("Device no longer exists, closing the tab");
                    }
                })
                .then(() => this._getSupportedOs())
                .then(() => {
                    if (this._isPropertyAllowed("detectedOs")) {
                        this._osIsSupported =
                            this._supportedOsList.indexOf(this._device.getDetectedOs()) > -1;
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    if (
                        !this.isBackupActionAvailable ||
                        (this._isPropertyAllowed("detectedOs") &&
                            !this._device.getDiscoveredOn()) ||
                        !this._osIsSupported
                    ) {
                        this.hideBarView(Registrations.DIE_VIEW_RESTORE);
                    }

                    if (
                        !this.isBackupActionAvailable ||
                        (this._isPropertyAllowed("detectedOs") &&
                            !this._device.getDiscoveredOn()) ||
                        !this._osIsSupported
                    ) {
                        this.hideBarAction(Registrations.DIE_ACTION_BACKUP);
                    }
                    // We hide CDA type buttons for meraki controllers:
                    if (
                        this._deviceType === "Controller" &&
                        this._deviceTypeName === "merakiCloudService"
                    ) {
                        this.hideBarAction(Registrations.DIE_ACTION_SET_DEFAULT_CONFIG);
                        this.hideBarView(Registrations.DIE_VIEW_AUDIT_SUMMARY);
                        this.hideBarView(Registrations.DIE_VIEW_SNAPSHOT);
                        this.hideBarView(Registrations.DIE_VIEW_CONFIG);
                        this.hideBarView(Registrations.DIE_VIEW_STATE);
                        this.hideBarView(Registrations.DIE_VIEW_COMPARE);
                    }

                    try {
                        if (hasPermission && this._device) {
                            return this._initTheOsVersion(this._device);
                        }
                        this._noNistOrCisco();
                        return Promise.resolve();
                    } catch (err) {
                        return Promise.resolve();
                    }
                })
                .then(() => {
                    return Promise.resolve(this._device);
                });
        } else if (deviceIds.length > 1) {
            this._deviceProm = this._initMultipleDevices(deviceIds);
            this._tabCmpnt.setTitle(`Multi-Edit (${deviceIds.length})`);
        }

        this._hideNetworkDiscoveryViews();
    }

    _noNistOrCisco() {
        this._isNistSupportEnabled = false;
        this._isCiscoSupportEnabled = false;
        this._hideVendorSpecificBarViews("all");
    }

    // Original version
    /*
    _initMultipleDevices(deviceIds) {
        const feed = this._DeviceEntity.startDevicesFeed(this._activeOrgId, {
            id: {$in: deviceIds},
        });
        return feed.whenInitialized().then(() => {
            const devices = feed.getCacheAsArray();
            feed.shutdown();
            if (devices.length > 0) {
                return devices;
            } else {
                return Promise.reject("Requested devices no longer exist");
            }
        });
    }
    */

    // Serialized version
    /*
    async _initMultipleDevices(deviceIds = []) {
        console.log("Start calls to backend");
        const chunkArray = (array, chunkSize) => {
            const chunks = [];
            for (let i = 0; i < array.length; i += chunkSize) {
                chunks.push(array.slice(i, i + chunkSize));
            }
            return chunks;
        };

        const chunkedDeviceIds = chunkArray(deviceIds, 100);

        let allDevices = [];

        for (const chunk of chunkedDeviceIds) {
            try {
                const devices = await this._DeviceEntity.loadDevices(this._activeOrgId, {id: {$in: chunk}});
                Array.prototype.push.apply(allDevices, devices);
            } catch (error) {
                return Promise.reject(error);
            }
        }

        if (allDevices.length > 0) {
            console.log("Done with calls to backend");
            return allDevices;
        } else {
            return Promise.reject("Requested devices no longer exist");
        }
    }
    */

    // Parallelized version
    _initMultipleDevices(deviceIds = []) {
        const chunkArray = (array, chunkSize) => {
            const chunks = [];
            for (let i = 0; i < array.length; i += chunkSize) {
                chunks.push(array.slice(i, i + chunkSize));
            }
            return chunks;
        };

        const chunkedDeviceIds = chunkArray(deviceIds, 250);

        const loadDeviceChunk = (chunk) => {
            return this._DeviceEntity.loadDevices(this._activeOrgId, {id: {$in: chunk}});
        };

        const devicePromises = chunkedDeviceIds.map((chunk) => loadDeviceChunk(chunk));

        return Promise.all(devicePromises).then((results) => {
            const devices = [];
            for (const result of results) {
                devices.push(...result);
            }
            if (devices.length > 0) {
                return devices;
            } else {
                return Promise.reject("Requested devices no longer exist");
            }
        });
    }

    /**
     * Hides the Exposure tab unless the tenant holds a TAI license.
     *
     * The gate promise is deliberately NOT returned into _initDeviceProm's chain. That chain
     * loads the device, starts its config feed and renders the editor; making it wait on a
     * license lookup would mean a license-service failure leaves the user staring at a blank
     * editor. The gate resolves on its own and hides fail-closed, so the worst case here is a
     * missing tab, never a missing editor.
     * @returns {void}
     */
    _hideExposureViewWhenUnlicensed() {
        applyExposureLicenseGate({
            licenseRestriction: this._licenseRestriction,
            logger: this._logger,
            hide: () => {
                this.hideBarView(Registrations.DIE_VIEW_EXPOSURE);
                // Moves the operator off the tab when it is the one they are sitting on.
                this.sendToDeviceIfEditorNotAvailable([Registrations.DIE_VIEW_EXPOSURE]);
            },
        });
    }

    _hideViewsForIot() {
        // Single list so the hidden views and the active-view fallback cannot drift apart.
        const hiddenViews = [
            Registrations.DIE_VIEW_CONFIG,
            Registrations.DIE_VIEW_EOX_BULLETIN,
            Registrations.DIE_VIEW_SMARTNET_DETAILS,
            Registrations.DIE_VIEW_AUDIT_SUMMARY,
            Registrations.DIE_VIEW_RESTORE,
            Registrations.DIE_VIEW_EXPOSURE,
        ];

        hiddenViews.forEach((view) => this.hideBarView(view));

        this.sendToDeviceIfEditorNotAvailable(hiddenViews);
    }

    _hideVendorSpecificBarViews(type = "cisco") {
        /*
        if(type === "cisco") {
            this.hideBarView(Registrations.DIE_VIEW_EOX_BULLETIN);
            this.hideBarView(Registrations.DIE_VIEW_SMARTNET_DETAILS);

            this.sendToDeviceIfEditorNotAvailable([
                Registrations.DIE_VIEW_EOX_BULLETIN,
                Registrations.DIE_VIEW_SMARTNET_DETAILS
            ]);

        } else {
            if (isAdvHidden) {
                this.hideBarView(Registrations.DIE_VIEW_ADVISORY_SUMMARY);

                this.sendToDeviceIfEditorNotAvailable([
                    Registrations.DIE_VIEW_ADVISORY_SUMMARY
                ]);
            }
        }

         */

        switch (type) {
            case "all":
                this.hideBarView(Registrations.DIE_VIEW_ADVISORY_SUMMARY);

                this.sendToDeviceIfEditorNotAvailable([Registrations.DIE_VIEW_ADVISORY_SUMMARY]);

                this.hideBarView(Registrations.DIE_VIEW_EOX_BULLETIN);
                this.hideBarView(Registrations.DIE_VIEW_SMARTNET_DETAILS);

                this.sendToDeviceIfEditorNotAvailable([
                    Registrations.DIE_VIEW_EOX_BULLETIN,
                    Registrations.DIE_VIEW_SMARTNET_DETAILS,
                ]);
                break;

            case "cisco":
                this.hideBarView(Registrations.DIE_VIEW_EOX_BULLETIN);
                this.hideBarView(Registrations.DIE_VIEW_SMARTNET_DETAILS);

                this.sendToDeviceIfEditorNotAvailable([
                    Registrations.DIE_VIEW_EOX_BULLETIN,
                    Registrations.DIE_VIEW_SMARTNET_DETAILS,
                ]);
                break;

            case "advisory":
                this.hideBarView(Registrations.DIE_VIEW_ADVISORY_SUMMARY);

                this.sendToDeviceIfEditorNotAvailable([Registrations.DIE_VIEW_ADVISORY_SUMMARY]);
                break;

            default:
        }
    }

    _hideNetworkDiscoveryViews() {
        this.hideBarView(Registrations.DIE_VIEW_NETWORK_DISCOVERY);
        this.sendToDeviceIfEditorNotAvailable([Registrations.DIE_VIEW_NETWORK_DISCOVERY]);
    }

    _hideBarViews() {
        this.hideBarView(Registrations.DIE_VIEW_DETAILS);
        this.hideBarView(Registrations.DIE_VIEW_CONFIG);
        this.hideBarView(Registrations.DIE_VIEW_ACTIVITY);
        this.hideBarView(Registrations.DIE_VIEW_EOX_BULLETIN);
        this.hideBarView(Registrations.DIE_VIEW_ADVISORY_SUMMARY);
        this.hideBarView(Registrations.DIE_VIEW_SMARTNET_DETAILS);

        this.sendToDeviceIfEditorNotAvailable([
            Registrations.DIE_VIEW_DETAILS,
            Registrations.DIE_VIEW_CONFIG,
            Registrations.DIE_VIEW_STATE,
            Registrations.DIE_VIEW_ACTIVITY,
            Registrations.DIE_VIEW_EOX_BULLETIN,
            Registrations.DIE_VIEW_ADVISORY_SUMMARY,
            Registrations.DIE_VIEW_SMARTNET_DETAILS,
        ]);
    }

    sendToDeviceIfEditorNotAvailable(editors) {
        if (editors.indexOf(this._editorState.fqn) > -1) {
            this.setEditorState({fqn: Registrations.DIE_VIEW_DETAILS});
            this._onSelectViewEditor(Registrations.DIE_VIEW_DETAILS);
        }
    }

    _reestablishConfigFeed() {
        const activeOrgId = this._userSession.activeOrganization.id;
        this._deviceConfigFeed.reestablishFeed(activeOrgId, {}, undefined, undefined, {
            Success: () => {
                this._deviceConfigFeedProm = this._deviceConfigFeed.whenInitialized().then(() => {
                    this.emit("DeviceConfigChangeFeedInitialized");
                });
            },
            Failure: (err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceInstanceEditor._reestablishConfigFeed error."
                );
            },
        });
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getDevice() {
        return this._device;
    }

    getOsVersion() {
        return this._osVersion;
    }

    whenCommandSetsAreReady() {
        return this._commandSetEntityDef
            .loadCommandSets(this._activeOrgId, {}, {})
            .then((_commandSets) => {
                this._commandSetMap = {};
                _commandSets.forEach((item) => {
                    Object.assign(this._commandSetMap, {[item.getId()]: item.getName()});
                });
                return Promise.resolve();
            });
    }

    whenDeviceIsReady() {
        if (!_.isUndefined(this._deviceProm)) {
            return this._deviceProm;
        } else {
            return Promise.reject("Can't load DeviceInstanceEditor._deviceProm");
        }
    }

    whenPolicyIsReady() {
        return this._ConfigAuditPolicyEntity
            .retrieve(this._activeOrgId, "ConfigAuditPolicy", this._auditPolicyId)
            .then((auditPolicy) => {
                this._auditPolicy = auditPolicy;
                // this._registerListenersOnPolicy();
                return auditPolicy;
            });
    }

    whenConceptItemsAreReady() {
        return this._ConfigAuditPolicyEntity
            .loadConceptItemLabelValues(this._activeOrgId)
            .then((conceptItems) => {
                this.conceptItems = conceptItems.reduce((result, item) => {
                    result[item.value] = item;
                    return result;
                }, {});
                this.os = this._DeviceEntity.osMap;
                return Promise.resolve();
            })
            .then(() => {
                return this._ConfigAuditPolicyEntity.loadRsiLabelValues(this._activeOrgId, {
                    "Config Modeling Only": {$ne: true},
                });
            })
            .then((rsiIds) => {
                this._rsiIds = rsiIds;
                this._rsiIdsMap = this._rsiIds.reduce((acc, item) => {
                    Object.assign(acc, {[item.value]: item});
                    return acc;
                }, {});
                return Promise.resolve();
            });
    }

    set conceptItems(newConceptItems) {
        this._conceptItems = _.cloneDeep(newConceptItems);
        return this;
    }

    get conceptItems() {
        return this._conceptItems;
    }

    getAuditPolicy() {
        return this._auditPolicy;
    }

    getDeviceConfig() {
        return this._deviceConfigFeed;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    get isCiscoSupportEnabled() {
        return this._isCiscoSupportEnabled;
    }

    get canRestoreBackup() {
        return _.get(this._userSession, "authorizedActions").indexOf("deviceRestoreRun") > -1;
    }
    get isBackupActionAvailable() {
        return _.get(this._userSession, "authorizedActions").indexOf("deviceBackupRun") > -1;
    }

    whenDeviceConfigIsReady() {
        return this._deviceProm.then(() => this._deviceConfigFeedProm);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    setDirtyState(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    setInstanceEditorStateProperty(name, val) {
        _.set(this._instanceEditorState, name, val);
    }

    getInstanceEditorStateProperty(name) {
        return _.get(this._instanceEditorState, name);
    }

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    shutdown() {
        const logger = this._logger;
        this._unregisterDocumentStateListener();
        this._unregisterCabinetListener();
        return super
            .shutdown()
            .then(() => {
                this._unregisterViewStateListener();
                if (!_.isUndefined(this._osVersionFeed)) {
                    this._unregisterVersionFeedListeners();
                }
                if (!_.isUndefined(this._device)) {
                    this._unregisterListenersOnDevice();
                }
            })
            .then(() => {
                return Promise.all([
                    _.invoke(this._auditPolicy, "shutdown") || Promise.resolve(),
                    _.invoke(this._deviceConfigFeed, "shutdown") || Promise.resolve(),
                    _.invoke(this._osVersionFeed, "shutdown") || Promise.resolve(),
                ]);
            })
            .then(() => {
                if (!_.isUndefined(this._deviceFeed)) {
                    return this._deviceFeed.shutdown();
                }
            })
            .then(() => {
                this._auditPolicy = undefined;
                this._tabCmpnt = undefined;
                this._deviceFeed = undefined;
                this._device = undefined;
                this._deviceConfigFeed = undefined;
                this._isCiscoSupportEnabled = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._tabId = undefined;
                this._activeOrgId = undefined;
                this._deviceProm = undefined;
                this._deviceName = undefined;
                this.currentEntityRemoved = undefined;
                this.currentEntityDeleteInProgress = undefined;
                this._osVersionFeed = undefined;
                this._storageFactory = undefined;
                this._userDataStorage = undefined;
                this._pagingFeed = undefined;
                this._commandSetEntityDef = undefined;
                this._commandSetMap = undefined;
                this._osIsSupported = undefined;
                this._supportedOsList = undefined;
                this._constructTypeEntityDef = undefined;
                this._conceptItems = undefined;
                this._rsiIds = undefined;
                this._rsiIdsMap = undefined;
            })
            .catch((err) => {
                logger.warn({error: err}, "DeviceInstanceEditor.shutdown() error");
            });
    }

    updateConfigFeedFilter(filterString) {
        if (_.isEmpty(filterString)) {
            this._reestablishConfigFeed();
        } else {
            this._reestablishConfigFeed(filterString);
        }
    }

    setViewActionText(buttonValue, buttonText) {
        const buttonControl = _.get(
            this._viewContainerElem.nextElementSibling.querySelectorAll(
                "button-control[value='" + buttonValue + "']"
            ),
            0
        );

        if (buttonControl) {
            buttonControl.innerHTML = buttonText;
        }
    }

    /**
     * Checks if we got object, or an array of objects, and converts it to the readable csv string
     * @param {Object | Array<Object>} convertData
     * @returns {String|null} CSV text, or null if serialization failed
     */
    convertToCSV(convertData) {
        try {
            return CsvSerializer.convertToCSV(convertData);
        } catch (err) {
            this._logger.warn({error: err}, "DeviceInstacne.convertToCSV() error");
            return null;
        }
    }

    saveFile(fileName, content, type) {
        const file = new this._steveIo.File(fileName, type, content);
        this._steveIo.fileFactory.saveFile(file);
    }

    saveCSVFile(fileName, content) {
        this.saveFile(fileName, content, "text/csv");
    }

    /**
     * this method is used in both deviceDetails and DeviceMultiEdit views
     * @param customFieldDef
     */
    generateCustomFieldJtype(customFieldDef) {
        const customFieldJtype = {};
        const customFieldId = customFieldDef.id;
        const type = customFieldDef.dataType.toLowerCase();
        const label = customFieldDef.label;
        const typeInfo = customFieldDef.typeInfo || {};

        customFieldJtype[customFieldId] = "";
        customFieldJtype[customFieldId + "@Display.label"] = label
            .split(" ")
            .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
            .join(" ");

        if (_.get(customFieldDef, "source", "") === "ControllerDiscoveryConfig") {
            customFieldJtype[customFieldId + "@Display.readOnly"] = true;
        }

        if (customFieldDef.description) {
            customFieldJtype[customFieldId + "@Display.description"] = customFieldDef.description;
        }

        switch (type) {
            case "number":
                customFieldJtype[customFieldId + "@Number"] = typeInfo.Number || {};
                break;
            case "string":
                customFieldJtype[customFieldId + "@String"] = typeInfo.String || {};
                break;
            case "boolean":
                customFieldJtype[customFieldId + "@value"] = false;
                break;
            case "enum": {
                const {ids = [], labels = [], multiSelect = false} = customFieldDef.typeInfo || {};
                let fieldJtypeDefinition;
                if (multiSelect) {
                    fieldJtypeDefinition = {
                        [`${customFieldId}`]: ids,
                        [`${customFieldId}@Display.tagList.labels`]: labels,
                        [`${customFieldId}@Display.tagList.minLineCount`]: 5,
                    };
                } else {
                    fieldJtypeDefinition = {
                        [`${customFieldId}`]: null,
                        [`${customFieldId}@Enum`]: ids,
                        [`${customFieldId}@Display.enum`]: labels,
                    };
                }
                Object.assign(customFieldJtype, fieldJtypeDefinition);
                break;
            }
        }
        return customFieldJtype;
    }

    clearPendingRelationshipsFor(relName, entity, silent) {
        let pendingRelationships = entity.getPendingRelationships();
        pendingRelationships =
            pendingRelationships &&
            pendingRelationships.filter((rel) => {
                if (rel.relName === relName) {
                    return false;
                }
                return true;
            });
        entity.clearPendingRelationships(silent);
        pendingRelationships.forEach((relation) => {
            entity._addPendingRelationship(
                relation.action,
                relation.relName,
                relation.rEntityType,
                relation.rEntityId
            );
        });
    }

    async _getSupportedOs() {
        try {
            const vendorOsMapEntity = await this._valueMap.loadVendorOsMapEntity(this._activeOrgId);
            const backupRestoreVendorOsMap =
                (vendorOsMapEntity &&
                    vendorOsMapEntity.filterVendorOsMapByOsProp(
                        "supportedFeatures.backupAndRestore",
                        true
                    )) ||
                {};
            const supportedOSs = Object.values(backupRestoreVendorOsMap).reduce((acc, item) => {
                for (const [os, osDef] of Object.entries(item.os)) {
                    acc.push(os, osDef.displayName);
                }
                return acc;
            }, []);
            this._supportedOsList = supportedOSs;
        } catch (error) {
            this._logger.warn({}, "Error loading supported OS");
            return Promise.resolve();
        }
    }

    _isPropertyAllowed(propertyName) {
        const schemaType = this._device.getSchemaType();
        const permits = this._device.getSchemaTypePermits();

        if (permits && permits[schemaType] && permits[schemaType].props) {
            return permits[schemaType].props[propertyName] === true;
        }
        return false;
    }
}

module.exports = DeviceInstanceEditor;

DeviceInstanceEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "$teve.registeredDetails",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.entities.VendorApiConfig",
    "$teve.entities.Device",
    "$teve.userSession",
    "$teve.entities.DeviceConfiguration",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.entities.OsVersion",
    "$teve.io",
    "$teve.entities.ConfigAuditPolicy",
    "$teve.storageFactory",
    "$teve.entities.CommandSet",
    "$teve.entities.ValueMap",
    "$teve.entities.ConstructType",
    // Appended, never inserted — this list is positional against the constructor signature.
    "$teve.licenseRestriction",
];

//Optional convenient way to set a tab icon.
DeviceInstanceEditor.$editorIcon = "common.default_device";
DeviceInstanceEditor.$editorIconType = "base-icon-gl";
